import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { CheckCircle2, Info, AlertCircle, Quote, ArrowRight, Plus, X, Brain, Zap, Target, Clock, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const ADHDAccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-fit">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <Icon className="text-[#8C2F12] w-6 h-6" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50">
              <div className="pt-4">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ADHD = () => {
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "ADHD-behandling i København | Metakognitiv Terapi & Hypnose",
    "description": "Få hjælp til ADHD uden medicin. Effektiv behandling af tankemylder, uro og koncentrationsbesvær med metakognitiv terapi og hypnose i Valby.",
    "medicalAudience": "Patient",
    "relevantSpecialty": "Psychiatry",
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "ADHD",
      "alternateName": "Attention Deficit Hyperactivity Disorder",
      "possibleTreatment": [
        {
          "@type": "MedicalTherapy",
          "name": "Metakognitiv Terapi"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Hypnose"
        }
      ]
    }
  };

  return (
    <ServiceLayout 
      title="ADHD-behandling – få ro og retning i hverdagen"
      subtitle="Oplever du tankemylder, uro eller manglende fokus? ADHD kan behandles effektivt uden medicin. Få hjælp til at skabe mental ro, struktur og balance."
      description="ADHD (Attention Deficit Hyperactivity Disorder) er en neurologisk forstyrrelse, som påvirker både opmærksomhed, impulsstyring og aktivitetsniveau. Mange voksne har levet med symptomerne i årevis uden at vide, at deres udfordringer skyldes en diagnose."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/e00d08c8-3659-4780-a6bb-c1831d85930b.png"
      metaDescription="Specialiseret ADHD-behandling for voksne. Kombineret metakognitiv terapi og hypnose hjælper dig med at dæmpe tankemylder og finde indre ro. Book tid i Valby."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Case Section */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Jeg var altid ti tanker foran, og samtidig bagefter med alt”</h3>
          <div className="text-white/80 mb-6 leading-relaxed space-y-4">
            <p>Mikkel følte, det var som at leve i konstant støj. Han havde prøvet alt, men roen kom aldrig.</p>
            <p>Efter et forløb med metakognitiv terapi og hypnose lærte han at give slip på det konstante selvfokus og reducere sine grublerier. Hypnosen hjalp ham med at mærke kroppen og finde indre ro.</p>
          </div>
          <p className="font-bold">— Mikkel, 38 år</p>
        </div>
      </div>

      {/* Hvad er ADHD hos voksne? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">ADHD hos voksne er mere end uro</h2>
        <div className="text-[#3B352F]/70 text-lg leading-relaxed space-y-4">
          <p>
            Hos voksne viser ADHD sig ofte som et konstant indre pres. Det er ikke nødvendigvis fysisk hyperaktivitet, men snarere en mental rastløshed, der aldrig holder pause.
          </p>
          <div className="bg-[#F4EFE7] p-6 rounded-xl border border-[#E8DDCE] mt-8">
            <h4 className="font-bold text-[#123F13] mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-[#8C2F12]" />
              Forskellige former for ADHD
            </h4>
            <ul className="space-y-3 text-sm text-[#3B352F]/80">
              <li>• <strong>ADD (uden hyperaktivitet):</strong> Ofte overset, især hos kvinder.</li>
              <li>• <strong>Hyperaktiv/impulsiv type:</strong> Tydelig ydre uro og impulsivitet.</li>
              <li>• <strong>Kombineret type:</strong> Både fokusvanskeligheder og hyperaktivitet.</li>
            </ul>
          </div>
          <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1 mt-6">
            <strong>Overlappende træk (AuDHD):</strong> Oplever du både træk fra ADHD og autisme? Det kaldes ofte AuDHD og kræver en særlig tilgang, hvor der tages højde for både behovet for struktur og behovet for stimulering. Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
          </p>
        </div>
      </div>

      {/* Symptomer */}
      <div className="mb-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Typiske symptomer på ADHD hos voksne</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <ADHDAccordionItem 
            title="Kognitive & Emotionelle" 
            icon={AlertCircle}
            isOpen={openItems.includes('cognitive')}
            onClick={() => toggleItem('cognitive')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Koncentrationsbesvær:</strong> Svært ved at fastholde fokus på opgaver.</li>
              <li>• <strong>Tankemylder:</strong> Tankerne myldrer konstant, hvilket gør det svært at prioritere.</li>
              <li>• <strong>Lav frustrationstolerance:</strong> Små ting føles uoverskuelige.</li>
              <li>• <strong>Humørsvingninger:</strong> Hurtige skift fra begejstring til irritation.</li>
            </ul>
          </ADHDAccordionItem>

          <ADHDAccordionItem 
            title="Adfærd & Overbelastning" 
            icon={Info}
            isOpen={openItems.includes('behavior')}
            onClick={() => toggleItem('behavior')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Indre og ydre uro:</strong> Kroppen er i konstant bevægelse.</li>
              <li>• <strong>Impulsivitet:</strong> Du afbryder andre eller træffer hurtige beslutninger.</li>
              <li>• <strong>Kaos i struktur:</strong> Tidsfornemmelsen er upræcis, og du kommer ofte for sent.</li>
              <li>• <strong>Mental træthed:</strong> Nervesystemet arbejder på højtryk for at sortere indtryk.</li>
            </ul>
          </ADHDAccordionItem>
        </div>
      </div>

      {/* Hvordan behandles ADHD hos mig? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvordan behandles ADHD hos mig?</h2>
        <p className="text-[#3B352F]/70 mb-8 text-lg">
          Behandlingen fokuserer på at give dig konkrete værktøjer til at håndtere din ADHD i hverdagen, så den ikke længere styrer dit liv.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <Brain size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Metakognitiv terapi</h3>
                <p className="text-[#3B352F]/70">
                  Jeg arbejder med dine tankemønstre. Du lærer konkrete værktøjer til at mindske overtænkning, selvkritik og mental uro, hvilket giver dig mere energi og fokus.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <Zap size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Hypnose</h3>
                <p className="text-[#3B352F]/70">
                  Hypnose skaber ro i nervesystemet. Det er særligt effektivt til at dæmpe den indre rastløshed og styrke din evne til at regulere dig selv i hverdagen.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#F4EFE7] p-8 rounded-2xl border border-[#E8DDCE]">
            <div className="space-y-6 text-[#3B352F]/70">
              <p className="flex gap-3">
                <Target className="w-5 h-5 text-[#8C2F12] shrink-0 mt-1" />
                <span><strong>Fokus på strategier:</strong> Jeg laver en realistisk plan, der hjælper dig med at fastholde struktur.</span>
              </p>
              <p className="flex gap-3">
                <Clock className="w-5 h-5 text-[#8C2F12] shrink-0 mt-1" />
                <span><strong>Hurtig effekt:</strong> De fleste mærker en markant forskel allerede efter de første par sessioner.</span>
              </p>
            </div>
            <p className="text-[#123F13] font-serif italic text-lg mt-8">
              “Målet er ikke at fjerne din ADHD, men at give dig kontrollen tilbage, så du kan bruge dine styrker.”
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Link & External Resources */}
      <div className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">FAQ - svar på dine spørgsmål</h3>
          <ul className="space-y-2 text-[#3B352F]/70 mb-6">
            <li>• Hvordan foregår en session?</li>
            <li>• Kan man behandle ADHD uden medicin?</li>
            <li>• Hvor hurtigt virker det?</li>
          </ul>
          <Link to="/faq" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Se alle spørgsmål og svar <ArrowRight size={16} />
          </Link>
        </div>
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">Vil du vide mere om ADHD?</h3>
          <p className="text-[#3B352F]/70 mb-6">
            Læs om symptomer, forskning og rettigheder på ADHD-foreningens hjemmeside.
          </p>
          <a href="https://adhd.dk" target="_blank" rel="noopener noreferrer" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Besøg ADHD-foreningen <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default ADHD;
